import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Trash2, Edit } from 'lucide-react';
import AppShell from '@/components/AppShell';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Goals = () => {
  const [goals, setGoals] = useState([]);
  const [showAddGoal, setShowAddGoal] = useState(false);
  const [showEditGoal, setShowEditGoal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [newGoalTitle, setNewGoalTitle] = useState('');
  const [editGoalTitle, setEditGoalTitle] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/goals`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      setGoals(response.data);
    } catch (error) {
      console.error('Failed to fetch goals:', error);
      toast.error('Failed to load goals');
    } finally {
      setLoading(false);
    }
  };

  const handleAddGoal = async () => {
    if (!newGoalTitle.trim()) {
      toast.error('Please enter a goal title');
      return;
    }

    try {
      const sessionToken = localStorage.getItem('session_token');
      await axios.post(
        `${API}/goals`,
        { title: newGoalTitle, progress: 0 },
        { headers: { 'Authorization': `Bearer ${sessionToken}` } }
      );
      toast.success('Goal added!');
      setShowAddGoal(false);
      setNewGoalTitle('');
      fetchGoals();
    } catch (error) {
      console.error('Failed to add goal:', error);
      toast.error('Failed to add goal');
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm('Are you sure you want to delete this goal?')) return;

    try {
      const sessionToken = localStorage.getItem('session_token');
      await axios.delete(`${API}/goals/${goalId}`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      toast.success('Goal deleted');
      fetchGoals();
    } catch (error) {
      console.error('Failed to delete goal:', error);
      toast.error('Failed to delete goal');
    }
  };

  const handleEditGoal = (goal) => {
    setEditingGoal(goal);
    setEditGoalTitle(goal.title);
    setShowEditGoal(true);
  };

  const handleUpdateGoal = async () => {
    if (!editGoalTitle.trim()) {
      toast.error('Please enter a goal title');
      return;
    }

    try {
      const sessionToken = localStorage.getItem('session_token');
      await axios.put(
        `${API}/goals/${editingGoal.goal_id}`,
        { title: editGoalTitle },
        { headers: { 'Authorization': `Bearer ${sessionToken}` } }
      );
      toast.success('Goal updated!');
      setShowEditGoal(false);
      setEditingGoal(null);
      setEditGoalTitle('');
      fetchGoals();
    } catch (error) {
      console.error('Failed to update goal:', error);
      toast.error('Failed to update goal');
    }
  };

  const handleUpdateProgress = async (goalId, newProgress) => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      await axios.put(
        `${API}/goals/${goalId}`,
        { progress: newProgress },
        { headers: { 'Authorization': `Bearer ${sessionToken}` } }
      );
      toast.success('Progress updated');
      fetchGoals();
    } catch (error) {
      console.error('Failed to update progress:', error);
      toast.error('Failed to update progress');
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-navy-900 border-r-transparent"></div>
            <p className="mt-4 text-gray-600">Loading goals...</p>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
              Your Goals
            </h1>
            <p className="text-gray-600">{goals.length} active goals</p>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="add-goal-btn"
              onClick={() => setShowAddGoal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors duration-200"
              style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
            >
              <Plus className="w-4 h-4" />
              Add Goal
            </Button>
          </div>
        </div>

        {/* Goals List */}
        {goals.length === 0 ? (
          <div className="bg-white p-12 rounded-lg border border-gray-200 text-center">
            <p className="text-gray-600 mb-4">No goals yet. Set your first goal to start tracking progress!</p>
            <Button
              onClick={() => setShowAddGoal(true)}
              className="px-6 py-3 rounded-lg font-medium transition-colors duration-200"
              style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
            >
              Set Your First Goal
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {goals.map((goal) => (
              <div
                key={goal.goal_id}
                data-testid={`goal-card-${goal.goal_id}`}
                className="bg-white p-4 md:p-6 rounded-lg border border-gray-200 hover:border-navy-200 hover:shadow-sm transition-all duration-200"
              >
                <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-3 mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base md:text-lg font-semibold mb-2 break-words" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
                      {goal.title}
                    </h3>
                    {goal.win_count > 0 && (
                      <p className="text-sm text-gray-600">
                        {goal.win_count} wins linked to this goal
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-lg md:text-xl font-bold" style={{ color: 'var(--navy-900)' }}>
                      {goal.progress}%
                    </span>
                    <button
                      onClick={() => handleEditGoal(goal)}
                      className="p-2 text-gray-400 hover:text-navy-900 transition-colors duration-200"
                      data-testid={`edit-goal-${goal.goal_id}`}
                      title="Edit goal"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteGoal(goal.goal_id)}
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors duration-200"
                      data-testid={`delete-goal-${goal.goal_id}`}
                      title="Delete goal"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="h-3 rounded-full transition-all duration-300"
                      style={{ width: `${goal.progress}%`, backgroundColor: 'var(--navy-900)' }}
                    />
                  </div>
                </div>

                {/* Progress Controls */}
                <div className="flex flex-wrap gap-2">
                  {[0, 25, 50, 75, 100].map((value) => (
                    <button
                      key={value}
                      onClick={() => handleUpdateProgress(goal.goal_id, value)}
                      className={`px-3 py-1.5 text-sm rounded transition-colors duration-200 ${
                        goal.progress === value
                          ? 'font-medium'
                          : 'text-gray-600 hover:bg-navy-50'
                      }`}
                      style={goal.progress === value ? { backgroundColor: 'var(--navy-900)', color: 'white' } : {}}
                      data-testid={`progress-${value}-btn`}
                    >
                      {value}%
                    </button>
                  ))}
                </div>

                {goal.progress === 100 && (
                  <div className="mt-4 p-3 rounded" style={{ backgroundColor: 'var(--cream-100)' }}>
                    <p className="text-sm font-medium" style={{ color: 'var(--navy-900)' }}>Goal completed. Nice work.</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Goal Dialog */}
      <Dialog open={showAddGoal} onOpenChange={setShowAddGoal}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
              Add New Goal
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>
                Goal Title
              </label>
              <Input
                data-testid="new-goal-input"
                value={newGoalTitle}
                onChange={(e) => setNewGoalTitle(e.target.value)}
                placeholder="e.g., Lead Q2 budget planning process"
                className="w-full"
              />
            </div>
            <div className="flex gap-3">
              <Button
                data-testid="save-goal-btn"
                onClick={handleAddGoal}
                className="flex-1 py-3 rounded-lg font-medium transition-colors duration-200"
                style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
              >
                Add Goal
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddGoal(false);
                  setNewGoalTitle('');
                }}
                className="px-6"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Edit Goal Dialog */}
      <Dialog open={showEditGoal} onOpenChange={setShowEditGoal}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
              Edit Goal
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>
                Goal Title
              </label>
              <Input
                data-testid="edit-goal-input"
                value={editGoalTitle}
                onChange={(e) => setEditGoalTitle(e.target.value)}
                placeholder="e.g., Improve leadership skills"
                className="w-full"
              />
            </div>
            <div className="flex gap-3">
              <Button
                data-testid="update-goal-btn"
                onClick={handleUpdateGoal}
                className="flex-1 py-3 rounded-lg font-medium transition-colors duration-200"
                style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
              >
                Update Goal
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowEditGoal(false);
                  setEditingGoal(null);
                  setEditGoalTitle('');
                }}
                className="px-6"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
};

export default Goals;